import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { ctaLabels, gallery } from '../data/content';
const ease = [0.23, 1, 0.32, 1] as const;
const arrowClass = 'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-ivory text-wine shadow-soft transition-colors duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-parchment focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/60 md:h-12 md:w-12';
export function Gallery() {
  const [index, setIndex] = useState(0);
  const total = gallery.length;
  const goTo = (next: number) => setIndex((next + total) % total);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(index - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(index + 1);
    }
  };
  return <section className="w-full bg-wine py-14 text-white md:py-20" data-id="element-73" data-mp="rbrrmxj9uf5a">
      <div className="mx-auto w-full max-w-5xl px-5 md:px-8" data-id="element-74" data-mp="wb4dpg2bji6t">
        <header className="mx-auto max-w-3xl text-center" data-id="element-75" data-mp="w9jh7kwg1p6b">
          <h2 className="font-serif text-3xl font-semibold leading-tight md:text-5xl" data-id="element-76" data-mp="113ff6ncpjc1h">
            Mira Octubre con María por dentro
          </h2>
          <p className="mt-4 text-sm font-semibold uppercase tracking-[0.16em] text-gold-light" data-id="element-77" data-mp="155sgmzkf8nsg">
            Reflexiones · Oraciones · Propósitos diarios
          </p>
        </header>

        <div role="group" aria-roledescription="carrusel" aria-label="Páginas de muestra de Octubre con María" tabIndex={0} onKeyDown={handleKeyDown} className="relative mx-auto mt-10 max-w-xl rounded-2xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/60 md:mt-12" data-id="element-78" data-mp="dew4y2a3kp6r">
          <div className="overflow-hidden rounded-2xl" data-id="element-79" data-mp="1xrlp9pseewn5">
            <motion.div className="flex cursor-grab active:cursor-grabbing" animate={{
            x: `-${index * 100}%`
          }} transition={{
            duration: 0.3,
            ease
          }} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.12} onDragEnd={(_, info) => {
            if (info.offset.x < -60) goTo(index + 1);
            if (info.offset.x > 60) goTo(index - 1);
          }} data-id="element-80" data-mp="3tvrql6ksftdb">
              {gallery.map((item, itemIndex) => <figure key={item.image} className="w-full shrink-0" aria-hidden={itemIndex !== index} data-id="element-81" data-mp="366yth6sfjfh4" data-mpi={itemIndex}>
                  <img src={item.image} alt={item.alt} className="pointer-events-none w-full rounded-2xl bg-ivory" draggable={false} loading={itemIndex === 0 ? 'eager' : 'lazy'} data-id="element-82" data-mp="3kw4y5aa1dxwe" />
                </figure>)}
            </motion.div>
          </div>

          <button type="button" onClick={() => goTo(index - 1)} aria-label="Página anterior" className={`${arrowClass} left-2 md:-left-5`} data-id="element-83" data-mp="12x54z0pftza8">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-84" data-mp="txievb25sfwh" />
          </button>
          <button type="button" onClick={() => goTo(index + 1)} aria-label="Página siguiente" className={`${arrowClass} right-2 md:-right-5`} data-id="element-85" data-mp="xyaoi5ey0mta">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-86" data-mp="1qmzlbs8yu5z8" />
          </button>
        </div>

        <p className="sr-only" aria-live="polite" data-id="element-87" data-mp="1cnzss9ic16xs">
          Página {index + 1} de {total}
        </p>

        <div className="mt-7 flex items-center justify-center gap-3" data-id="element-88" data-mp="1hm1llm5k8kmg">
          {gallery.map((item, itemIndex) => <button key={item.image} type="button" onClick={() => goTo(itemIndex)} aria-label={`Ver página ${itemIndex + 1} de ${total}`} aria-current={itemIndex === index} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold/60 ${itemIndex === index ? 'w-8 bg-white' : 'w-2.5 bg-white/35 hover:bg-white/60'}`} data-id="element-89" data-mp="179sf70lb9c3" data-mpi={itemIndex} />)}
        </div>

        <p className="mx-auto mt-8 max-w-xl text-center text-white/80" data-id="element-90" data-mp="14eu99pdvz2na">
          Puedes utilizarlo desde tu celular, tablet o computadora, o imprimirlo si prefieres tenerlo
          en papel.
        </p>

        <p className="mt-5 text-center font-serif text-2xl font-semibold italic text-gold-light md:text-3xl" data-id="element-91" data-mp="vdw4h4vmir3n">
          Un día. Un momento. Un propósito.
        </p>

        <div className="mt-8 flex justify-center" data-id="element-92" data-mp="3g5zedqr0bp3w">
          <CtaButton variant="secondary" size="md" className="w-full max-w-sm sm:w-auto" data-id="element-93" data-mp="3g6r6uspb56py">
            {ctaLabels.primary}
          </CtaButton>
        </div>
      </div>
    </section>;
}